/**
 * Composant de panneau de filtres réutilisable
 */

import { html, unsafeCSS, PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { WidgetLanguage, FilterId } from "../types/widgets.d";
import { TranslationService } from "../services/TranslationService";
import { ApidaeVisualHost } from "./ApidaeVisualHost";
import { FilterGroup } from "../types/panel.d";
import filterPanelStyles from "../styles/filter-panel.css?inline";
import "./DatePicker";
import "./Combobox";
import type { DateRange } from "./DatePicker";
import type { ComboboxOption } from "./Combobox";

@customElement("apidae-filter-panel")
export class FilterPanel extends ApidaeVisualHost {
  static styles = unsafeCSS(filterPanelStyles);

  @property({ type: Array })
  filterGroups: FilterGroup[] = [];

  @property({ type: String })
  language: WidgetLanguage = WidgetLanguage.FR;

  /** Filtres actifs (sélections) fournis par le parent ; utilisé pour l'affichage. */
  @property({ type: Object })
  activeFilters: Map<FilterId, Set<string>> = new Map();

  @property({ type: Object })
  dateRange: DateRange = { dateDebut: null, dateFin: null };

  /** Facet en cours de chargement "tout" (boucle voir plus). Désactive le bouton. */
  @property({ type: String, attribute: false })
  loadingMoreFacetId: string | null = null;

  @state()
  private expandedGroups: Set<FilterId> = new Set();

  @state()
  private internalDateRange: DateRange = { dateDebut: null, dateFin: null };

  @state()
  private initialized: boolean = false;

  connectedCallback() {
    super.connectedCallback();
    // Synchroniser l'état interne des dates avec la propriété dateRange
    this.internalDateRange = { ...this.dateRange };
    // Ouvrir tous les groupes par défaut à l'initialisation
    this.initializeExpandedGroups();
  }

  private initializeExpandedGroups() {
    if (!this.initialized && this.filterGroups.length > 0) {
      this.filterGroups.forEach((group) => {
        this.expandedGroups.add(group.id as FilterId);
      });
      this.initialized = true;
    }
  }

  updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    // Synchroniser l'état interne des dates avec la propriété dateRange
    if (changedProperties.has("dateRange")) {
      this.internalDateRange = { ...this.dateRange };
    }
    // À l'initialisation, s'assurer que tous les groupes sont dépliés
    if (changedProperties.has("filterGroups") && !this.initialized) {
      this.initializeExpandedGroups();
    }
  }

  private toggleGroup(groupId: FilterId) {
    if (this.expandedGroups.has(groupId)) {
      this.expandedGroups.delete(groupId);
    } else {
      this.expandedGroups.add(groupId);
    }
    this.requestUpdate();
  }

  private toggleFilter(groupId: FilterId, optionId: string) {
    const group = this.filterGroups.find((g) => g.id === groupId);
    const newFilters = this.cloneFilters(this.activeFilters);
    const selected = newFilters.get(groupId) ?? new Set<string>();
    if (!newFilters.has(groupId)) newFilters.set(groupId, selected);

    if (group?.multiple) {
      if (selected.has(optionId)) selected.delete(optionId);
      else selected.add(optionId);
    } else {
      if (selected.has(optionId)) selected.clear();
      else {
        selected.clear();
        selected.add(optionId);
      }
    }
    this.emitFilterChange({ filters: newFilters });
  }

  private cloneFilters(
    m: Map<FilterId, Set<string>>,
  ): Map<FilterId, Set<string>> {
    const out = new Map<FilterId, Set<string>>();
    m.forEach((v, k) => out.set(k, new Set(v)));
    return out;
  }

  private resetFilters() {
    this.internalDateRange = { dateDebut: null, dateFin: null };
    this.emitFilterChange({
      filters: new Map(),
      dateRange: this.internalDateRange,
    });
  }

  /**
   * Méthode publique pour réinitialiser les filtres depuis l'extérieur
   */
  public clearFilters() {
    this.resetFilters();
  }

  private emitFilterChange(overrides?: {
    filters?: Map<FilterId, Set<string>>;
    dateRange?: DateRange;
  }) {
    const filters = overrides?.filters ?? this.cloneFilters(this.activeFilters);
    const dateRange = overrides?.dateRange ?? this.internalDateRange;

    const filtersWithDates = this.cloneFilters(filters);
    if (dateRange.dateDebut || dateRange.dateFin) {
      filtersWithDates.set(
        FilterId.OUVERTURE,
        new Set([dateRange.dateDebut || "", dateRange.dateFin || ""]),
      );
    } else {
      filtersWithDates.delete(FilterId.OUVERTURE);
    }

    this.dispatchEvent(
      new CustomEvent("filter-change", {
        detail: { filters: filtersWithDates, dateRange },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private isFilterSelected(groupId: FilterId, optionId: string): boolean {
    return this.activeFilters.get(groupId)?.has(optionId) || false;
  }

  private t(key: string): string {
    return TranslationService.translate(key, this.language);
  }

  render() {
    return html`
      <div class="filter-header">
        <span class="filter-by-label">${this.t("filterBy")}</span>
      </div>

      <div class="filter-groups-container">
        ${this.filterGroups.map((group) => {
          // Filtre OUVERTURE : DatePicker
          if (group.id === FilterId.OUVERTURE) {
            return html`
              <div class="filter-group">
                <div
                  class="filter-group-header"
                  @click=${() => this.toggleGroup(group.id)}
                >
                  <span class="filter-group-label">${group.label}</span>
                  <span
                    class="filter-group-toggle ${this.expandedGroups.has(
                      group.id,
                    )
                      ? "expanded"
                      : ""}"
                    >></span
                  >
                </div>
                <div
                  class="filter-group-options ${this.expandedGroups.has(
                    group.id,
                  )
                    ? "expanded"
                    : ""}"
                >
                  <apidae-date-picker
                    .value=${this.internalDateRange}
                    .language=${this.language}
                    .visualCustomization=${this.visualCustomization}
                    @date-change=${(e: CustomEvent) => {
                      this.internalDateRange = e.detail;
                      this.emitFilterChange();
                    }}
                  ></apidae-date-picker>
                </div>
              </div>
            `;
          }

          // Filtre COMMUNES : Combobox avec toutes les valeurs (chargement complet auto, pas de bouton Voir plus)
          if (group.id === FilterId.COMMUNES) {
            const comboboxOptions: ComboboxOption[] = group.options.map(
              (opt) => ({
                id: opt.id,
                label: opt.label,
                count: opt.count,
              }),
            );
            const selectedCommuneIds = this.activeFilters.get(group.id)
              ? Array.from(this.activeFilters.get(group.id)!)
              : [];

            return html`
              <div class="filter-group">
                <div
                  class="filter-group-header"
                  @click=${() => this.toggleGroup(group.id)}
                >
                  <span class="filter-group-label">${group.label}</span>
                  <span
                    class="filter-group-toggle ${this.expandedGroups.has(
                      group.id,
                    )
                      ? "expanded"
                      : ""}"
                    >></span
                  >
                </div>
                <div
                  class="filter-group-options ${this.expandedGroups.has(
                    group.id,
                  )
                    ? "expanded"
                    : ""}"
                >
                  <apidae-combobox
                    .options=${comboboxOptions}
                    .selectedValues=${selectedCommuneIds}
                    .language=${this.language}
                    .visualCustomization=${this.visualCustomization}
                    @selection-change=${(e: CustomEvent) => {
                      const newFilters = this.cloneFilters(this.activeFilters);
                      newFilters.set(
                        group.id,
                        new Set<string>(e.detail.selectedValues),
                      );
                      this.emitFilterChange({ filters: newFilters });
                    }}
                  ></apidae-combobox>
                </div>
              </div>
            `;
          }

          // Filtres normaux : checkboxes (afficher 10, 20, … + "Voir plus" si hasMore)
          const visibleOptions =
            group.hasMore && group.options.length > 0
              ? group.options.slice(0, group.options.length - 1)
              : group.options;
          return html`
            <div
              class="filter-group ${group.options.length > 0
                ? ""
                : "group-hidden"}"
            >
              <div
                class="filter-group-header"
                @click=${() => this.toggleGroup(group.id)}
              >
                <span class="filter-group-label">${group.label}</span>
                <span
                  class="filter-group-toggle ${this.expandedGroups.has(group.id)
                    ? "expanded"
                    : ""}"
                  >></span
                >
              </div>

              <div
                class="filter-group-options ${this.expandedGroups.has(group.id)
                  ? "expanded"
                  : ""}"
              >
                ${visibleOptions.map(
                  (option) => html`
                    <div
                      class="filter-option"
                      @click=${() => this.toggleFilter(group.id, option.id)}
                    >
                      <div
                        class="filter-checkbox ${this.isFilterSelected(
                          group.id,
                          option.id,
                        )
                          ? "checked"
                          : ""}"
                      ></div>
                      <span class="filter-option-label">${option.label}</span>
                      ${option.count !== undefined
                        ? html`
                            <span class="filter-option-count"
                              >${option.count}</span
                            >
                          `
                        : ""}
                    </div>
                  `,
                )}
                ${group.hasMore && group.facet
                  ? html`
                      <button
                        type="button"
                        class="filter-load-more"
                        ?disabled=${this.loadingMoreFacetId === group.facet}
                        @click=${(e: Event) => {
                          e.stopPropagation();
                          if (this.loadingMoreFacetId) return;
                          this.dispatchEvent(
                            new CustomEvent("load-more-facet", {
                              detail: { facetId: group.facet },
                              bubbles: true,
                              composed: true,
                            }),
                          );
                        }}
                      >
                        ${this.loadingMoreFacetId === group.facet
                          ? this.t("loading") || "Chargement..."
                          : this.t("seeMore") || "Voir plus"}
                      </button>
                    `
                  : ""}
              </div>
            </div>
          `;
        })}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "apidae-filter-panel": FilterPanel;
  }
}
